// SUB-OFFICE 7 — audio skeleton. WebAudio, entirely procedural, DEFAULT OFF,
// and never before a user gesture: the browser wants a gesture, the Bureau
// wants a form, and both are satisfied by FORM 3 (intake slice). Until then,
// the M key provisionally grants/revokes ambience (a gesture, and a form of
// consent the Bureau tolerates).
//
// This slice ships: master bus (gain -> compressor), the mains HUM voice,
// footstep taps, and small mechanism voices (tick/flap/servo/thunk) wired to
// bus events. The full 5-voice architecture + per-room sends land later.

import type { EventBus } from './entities/entities';

export class BureauAudio {
  enabled = false;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private humGain: GainNode | null = null;
  private humLp1: BiquadFilterNode | null = null;
  private humLp2: BiquadFilterNode | null = null;
  private fifthGain: GainNode | null = null;
  private shaper: WaveShaperNode | null = null;
  private lastTick = 0;
  private lastServo = 0;
  private lastClunk = 0;

  constructor(private bus: EventBus) {
    bus.on('audio:toggle', () => this.toggle());
    bus.on('audio:tick', () => this.tick());
    bus.on('audio:flap', () => this.flap());
    bus.on('audio:servo', () => this.servo());
    bus.on('audio:step', () => this.step());
    bus.on('audio:thunk', () => this.thunk());
    bus.on('audio:buzz', () => this.buzz());
    bus.on('audio:clunk', (p) => this.clunk((p as { player?: boolean } | undefined)?.player === true));
    bus.on('audio:adjudication', (p) => this.setAdjudication((p as { on: boolean }).on));
    bus.on('audio:unfit', () => this.unfitSlam());
  }

  /** FORM 3 — CONSENT TO AMBIENCE: the explicit grant/revoke (no toast). */
  setConsent(granted: boolean): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    if (ctx.state === 'suspended') void ctx.resume();
    this.enabled = granted;
    this.master.gain.linearRampToValueAtTime(granted ? 0.55 : 0.0, ctx.currentTime + 0.4);
  }

  /** The building leans closer: hum lowpass sweeps 180 -> 320 Hz and a quiet
   *  sine a fifth above joins for the duration of the adjudication. */
  private setAdjudication(on: boolean): void {
    if (!this.ctx || !this.humLp1 || !this.humLp2 || !this.fifthGain) return;
    const t = this.ctx.currentTime;
    const freq = on ? 320 : 180;
    this.humLp1.frequency.linearRampToValueAtTime(freq, t + 0.8);
    this.humLp2.frequency.linearRampToValueAtTime(freq, t + 0.8);
    this.fifthGain.gain.linearRampToValueAtTime(on ? 0.022 : 0, t + 0.8);
  }

  /** window summons buzz: three insistent grains, official in tone. */
  private buzz(): void {
    if (!this.enabled) return;
    for (let i = 0; i < 3; i++) {
      window.setTimeout(() => this.burst(118, 0.11, 'sawtooth', 0.09, 'bandpass', 620), i * 140);
    }
  }

  /** tube clunk: 80ms noise 300-900Hz over a 50Hz thump, per fixture ring. */
  private clunk(isPlayer: boolean): void {
    const now = performance.now();
    if (now - this.lastClunk < 90) return;
    this.lastClunk = now;
    this.noiseBurst(0.08, isPlayer ? 0.12 : 0.05, 'bandpass', 600);
    this.burst(50, 0.07, 'sine', isPlayer ? 0.2 : 0.08);
  }

  /** UNFIT TRIBUTE: the window slam — the only distorted sound in the game. */
  private unfitSlam(): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    if (!this.shaper) {
      this.shaper = ctx.createWaveShaper();
      const curve = new Float32Array(256);
      for (let i = 0; i < 256; i++) {
        const x = (i / 128) - 1;
        curve[i] = Math.tanh(x * 6);
      }
      this.shaper.curve = curve;
      this.shaper.connect(this.master);
    }
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(150, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(30, ctx.currentTime + 0.16);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.9, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
    o.connect(g).connect(this.shaper);
    o.start();
    o.stop(ctx.currentTime + 0.25);
    this.noiseBurst(0.05, 0.3, 'lowpass', 1400);
  }

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      this.ctx = new AudioContext();
    } catch {
      return null; // no audio hardware; the Bureau does not mention it
    }
    const ctx = this.ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0.0;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 4;
    this.master.connect(comp).connect(ctx.destination);

    // HUM: two detuned saws 50/100Hz through a 24dB lowpass at 180Hz
    const lp1 = ctx.createBiquadFilter();
    lp1.type = 'lowpass';
    lp1.frequency.value = 180;
    lp1.Q.value = 0.7;
    const lp2 = ctx.createBiquadFilter();
    lp2.type = 'lowpass';
    lp2.frequency.value = 180;
    this.humLp1 = lp1;
    this.humLp2 = lp2;
    this.humGain = ctx.createGain();
    this.humGain.gain.value = 0.05;
    for (const [freq, det] of [[50, 0], [100, 4]] as [number, number][]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = freq;
      o.detune.value = det;
      o.connect(lp1);
      o.start();
    }
    lp1.connect(lp2).connect(this.humGain).connect(this.master);
    // the adjudication fifth: silent until the building leans in
    this.fifthGain = ctx.createGain();
    this.fifthGain.gain.value = 0;
    const fifth = ctx.createOscillator();
    fifth.type = 'sine';
    fifth.frequency.value = 150;
    fifth.connect(this.fifthGain).connect(this.master);
    fifth.start();
    // 0.1Hz breathing LFO on the hum
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.1;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.015;
    lfo.connect(lfoGain).connect(this.humGain.gain);
    lfo.start();
    return ctx;
  }

  toggle(): void {
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    if (ctx.state === 'suspended') void ctx.resume();
    this.enabled = !this.enabled;
    this.master.gain.linearRampToValueAtTime(this.enabled ? 0.55 : 0.0, ctx.currentTime + 0.4);
    this.bus.emit('toast', this.enabled
      ? 'FORM 3 — CONSENT TO AMBIENCE: PROVISIONALLY GRANTED. THE FULL FORM AWAITS YOU AT INTAKE.'
      : 'FORM 3 — CONSENT TO AMBIENCE: WITHDRAWN. THE HALL CONTINUES WITHOUT YOUR HEARING IT.');
  }

  private burst(
    freq: number,
    dur: number,
    type: OscillatorType,
    vol: number,
    filterType: BiquadFilterType | null = null,
    filterFreq = 1000,
    freqEnd?: number
  ): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    if (freqEnd !== undefined) o.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), ctx.currentTime + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    let node: AudioNode = o;
    if (filterType) {
      const f = ctx.createBiquadFilter();
      f.type = filterType;
      f.frequency.value = filterFreq;
      if (filterType === 'bandpass') f.Q.value = 4;
      node.connect(f);
      node = f;
    }
    node.connect(g).connect(this.master);
    o.start();
    o.stop(ctx.currentTime + dur + 0.02);
  }

  private noiseBurst(dur: number, vol: number, filterType: BiquadFilterType, filterFreq: number): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = filterFreq;
    const g = ctx.createGain();
    g.gain.value = vol;
    src.connect(f).connect(g).connect(this.master);
    src.start();
  }

  /** dot-matrix: 4ms square grains, highpassed 2kHz, one per character. */
  private tick(): void {
    const now = performance.now();
    if (now - this.lastTick < 18) return;
    this.lastTick = now;
    this.burst(1150, 0.02, 'square', 0.05, 'highpass', 2000);
  }

  private flap(): void {
    this.noiseBurst(0.03, 0.09, 'bandpass', 1200);
  }

  /** servo: saw glide 220->180, bandpass 800 Q4, stepped to the 7.5deg quanta. */
  private servo(): void {
    const now = performance.now();
    if (now - this.lastServo < 40) return;
    this.lastServo = now;
    this.burst(220, 0.06, 'sawtooth', 0.05, 'bandpass', 800, 180);
  }

  private step(): void {
    this.noiseBurst(0.05, 0.05, 'lowpass', 480);
  }

  /** stamp/door thunk: sine 140->38 over 90ms + 10ms noise transient. */
  private thunk(): void {
    this.burst(140, 0.09, 'sine', 0.5, null, 0, 38);
    this.noiseBurst(0.012, 0.12, 'lowpass', 900);
  }
}
